import { Button } from '@factorialco/deprecated-design-system'
import {
  CreateSalesDocumentTemplateDocument,
  LegalEntitySelectorInitialValuesDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { SalesDocumentTemplateForm } from 'modules/finance/activities/salesDocumentTemplate/components/SalesDocumentTemplateForm'
import { SalesDocumentTemplateFormValues } from 'modules/finance/activities/salesDocumentTemplate/components/types'

export const SalesDocumentTemplateCreateModal = ({
  isOpen,
  onClose,
  refetchQuery,
  legalEntityId,
}: {
  isOpen: boolean
  onClose: () => void
  refetchQuery?: string
  legalEntityId?: number
}) => {
  const {
    company: { id: companyId, name: companyName },
  } = useCurrentSession()
  const { data: legalEntityData } = useQuery(LegalEntitySelectorInitialValuesDocument, {
    variables: {
      companyId: companyId,
      ids: legalEntityId ? [legalEntityId] : [],
    },
    skip: !legalEntityId || !companyId,
  })

  const [createSalesDocumentTemplate, { loading: creating }] = useMutation(
    CreateSalesDocumentTemplateDocument,
    {
      extractorFn: (data) => data.financeMutations.createFinanceSalesDocumentTemplate,
      refetchQueries: refetchQuery ? [refetchQuery] : undefined,
    }
  )

  const form = useForm<SalesDocumentTemplateFormValues>({
    defaultValues: {
      name: '',
      footer: '',
      logoFile: null,
      default: false,
      legalEntity: null,
    },
    validateOnChange: true,
    validate: (data) => {
      const errors: FormErrors<SalesDocumentTemplateFormValues> = {}

      if (!data.name) {
        errors.name = i18n.t('common.validations.empty')
      }

      if (!data.legalEntity) {
        errors.legalEntity = i18n.t('finance.sales_document_templates.modal.legal_entity_required')
      }

      return errors
    },
    onSubmit: async (values) => {
      const formLegalEntityId = values.legalEntity?.id
      if (!formLegalEntityId) {
        return {
          errors: [],
          success: false,
        }
      }

      const result = await createSalesDocumentTemplate({
        variables: {
          legalEntityId: formLegalEntityId,
          name: values.name,
          footer: values.footer,
          logoFile: values.logoFile,
          default: values.default,
        },
      })

      const handled = handleResult(result, (parsedErrors) => {
        if (parsedErrors.name || parsedErrors.footer || parsedErrors.logoFile) {
          form.setErrors({
            name: parsedErrors.name,
            footer: parsedErrors.footer,
            logoFile: parsedErrors.logoFile,
          })
        } else {
          app.setFlash({
            type: 'wrong',
            message: parsedErrors.errors ?? i18n.t('errors.generic_error'),
          })
        }
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('finance.sales_document_templates.modal.create_success'),
          time: 2000,
        })
        userClose()
        return { success: true, data: values }
      } else {
        return {
          errors: [],
          success: false,
        }
      }
    },
  })

  useUnsafeEffect(() => {
    if (legalEntityData?.companies.legalEntities.length) {
      form.setFieldValue('legalEntity', legalEntityData.companies.legalEntities[0])
    }
  }, [legalEntityData])

  const userClose = () => {
    form.resetForm()
    onClose()
  }

  if (!isOpen) return null

  return (
    <Modal
      illustration="template"
      title={i18n.t('finance.sales_document_templates.modal.new_template.title')}
      description={i18n.t('finance.sales_document_templates.modal.new_template.description')}
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button type="secondary" onClick={userClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit disabled={creating}>
            {i18n.t('common.create')}
          </Button>
        </>
      }
    >
      <SalesDocumentTemplateForm form={form} companyName={companyName} />
    </Modal>
  )
}
